/**
 * Gift matching pools helpers
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import constants from '../../../constants';
import { formatDateWithPattern } from '../../../utils/time/formatDateWithPattern';
import { GiftMatchingPoolApiError } from './api';
import { apiErrorMessageKeys, i18nKeys } from './constants';
import {
  AccountBasic,
  ConditionFormValues,
  conditionKey,
  ConditionOperator,
  conditionOperator,
  GiftMatchingCondition,
  GiftMatchingDonation,
  PoolFormValues,
  PoolSettings
} from './types';

dayjs.extend(utc);

/** An API UTC date time in the admin's local date format. */
export const formatPoolDate = (value: string | null | undefined): string =>
  value
    ? formatDateWithPattern(
        dayjs.utc(value).local(),
        constants.DEFAULT_DATE_FORMAT,
        '-'
      )
    : '';

/** A condition's wall-clock date time in the admin's date format, with the time. */
export const formatConditionDateTime = (value: string): string =>
  value ? dayjs(value).format(`${constants.DEFAULT_DATE_FORMAT} HH:mm`) : '';

const pageConditionKeys: string[] = [
  conditionKey.pageCollection,
  conditionKey.pageFundraiser,
  conditionKey.pageTeam
];

/** Fundraiser, team and collection conditions point at pages inside one campaign. */
export const hasPageConditions = (
  conditions: { key: string }[] | null | undefined
): boolean => (conditions ?? []).some((c) => pageConditionKeys.includes(c.key));

/** A `datetime-local` value as the wall-clock string the API stores. */
export const toApiDateTime = (value: string): string =>
  value.length === 16 ? `${value}:00` : value;

/** An API date time as a `datetime-local` value. */
export const fromApiDateTime = (
  value: string | number | null | undefined
): string => (typeof value === 'string' ? value.slice(0, 16) : '');

const asText = (value: string | number | null | undefined): string =>
  value === null || value === undefined ? '' : String(value);

export const emptyConditionValues = (): ConditionFormValues => ({
  campaignTotal: { enabled: false, value: '' },
  donationAmount: {
    enabled: false,
    from: '',
    operator: conditionOperator.lesserOrEqualThan,
    to: ''
  },
  donationCount: { enabled: false, value: '' },
  endsAt: { enabled: false, value: '' },
  giftMatchingTotal: { enabled: false, value: '' },
  startsAt: { enabled: false, value: '' }
});

export const toConditionFormValues = (
  conditions: GiftMatchingCondition[] | null | undefined
): ConditionFormValues => {
  const values = emptyConditionValues();
  (conditions ?? []).forEach((condition) => {
    const [first, second] = condition.value ?? [];
    switch (condition.key) {
      case conditionKey.dateTime:
        if (condition.operator === conditionOperator.lesserOrEqualThan) {
          values.endsAt = { enabled: true, value: fromApiDateTime(first) };
        } else if (condition.operator === conditionOperator.between) {
          values.startsAt = { enabled: true, value: fromApiDateTime(first) };
          values.endsAt = { enabled: true, value: fromApiDateTime(second) };
        } else {
          values.startsAt = { enabled: true, value: fromApiDateTime(first) };
        }
        break;
      case conditionKey.giftMatchingTotal:
        values.giftMatchingTotal = { enabled: true, value: asText(first) };
        break;
      case conditionKey.campaignTotal:
        values.campaignTotal = { enabled: true, value: asText(first) };
        break;
      case conditionKey.donationCount:
        values.donationCount = { enabled: true, value: asText(first) };
        break;
      case conditionKey.donationAmount: {
        const operator: ConditionOperator =
          condition.operator === conditionOperator.between
            ? conditionOperator.between
            : condition.operator === conditionOperator.greaterOrEqualThan
              ? conditionOperator.greaterOrEqualThan
              : conditionOperator.lesserOrEqualThan;
        values.donationAmount = {
          enabled: true,
          from: asText(first),
          operator,
          to: asText(second)
        };
        break;
      }
      default:
        break;
    }
  });
  return values;
};

export const toApiConditions = (
  values: ConditionFormValues
): GiftMatchingCondition[] => {
  const conditions: GiftMatchingCondition[] = [];
  if (values.startsAt.enabled) {
    conditions.push({
      key: conditionKey.dateTime,
      operator: conditionOperator.greaterOrEqualThan,
      value: [toApiDateTime(values.startsAt.value)]
    });
  }
  if (values.endsAt.enabled) {
    conditions.push({
      key: conditionKey.dateTime,
      operator: conditionOperator.lesserOrEqualThan,
      value: [toApiDateTime(values.endsAt.value)]
    });
  }
  (['giftMatchingTotal', 'campaignTotal', 'donationCount'] as const).forEach(
    (key) => {
      if (values[key].enabled) {
        conditions.push({
          key: conditionKey[key],
          operator: conditionOperator.lesserOrEqualThan,
          value: [Number(values[key].value)]
        });
      }
    }
  );
  const amount = values.donationAmount;
  if (amount.enabled) {
    conditions.push({
      key: conditionKey.donationAmount,
      operator: amount.operator,
      value:
        amount.operator === conditionOperator.between
          ? [Number(amount.from), Number(amount.to)]
          : [Number(amount.from)]
    });
  }
  return conditions;
};

/** The i18n keys for whatever stops these conditions being saved, empty when they are valid. */
export const conditionErrors = (values: ConditionFormValues): string[] => {
  const toggles = [
    values.startsAt,
    values.endsAt,
    values.giftMatchingTotal,
    values.campaignTotal,
    values.donationCount
  ];
  const amount = values.donationAmount;
  if (!toggles.some((t) => t.enabled) && !amount.enabled) {
    return [i18nKeys.ERRORS.CONDITIONS_REQUIRED];
  }
  const errors: string[] = [];
  const missingToggle = toggles.some((t) => t.enabled && !t.value.trim());
  const isBetween = amount.operator === conditionOperator.between;
  const missingAmount =
    amount.enabled && (!amount.from.trim() || (isBetween && !amount.to.trim()));
  if (missingToggle || missingAmount) {
    errors.push(i18nKeys.ERRORS.CONDITION_VALUE_REQUIRED);
  }
  const numbers = [
    values.giftMatchingTotal,
    values.campaignTotal,
    values.donationCount
  ]
    .filter((t) => t.enabled)
    .map((t) => t.value);
  if (amount.enabled) {
    numbers.push(amount.from, ...(isBetween ? [amount.to] : []));
  }
  if (numbers.some((value) => value.trim() && !/^\d+$/.test(value.trim()))) {
    errors.push(i18nKeys.ERRORS.CONDITION_VALUE_INVALID);
  }
  if (
    amount.enabled &&
    isBetween &&
    amount.from.trim() &&
    amount.to.trim() &&
    Number(amount.from) >= Number(amount.to)
  ) {
    errors.push(i18nKeys.ERRORS.CONDITION_RANGE);
  }
  return errors;
};

export const toPoolFormValues = (source: {
  conditions: GiftMatchingCondition[] | null;
  donation: GiftMatchingDonation | null;
  enabled: boolean;
  mainImagePath: string | null;
  multiplier: number;
  name: string;
}): PoolFormValues => ({
  conditions: toConditionFormValues(source.conditions),
  donationFrom: source.donation?.from ?? '',
  donationMessage: source.donation?.message ?? '',
  enabled: source.enabled,
  mainImagePath: source.mainImagePath,
  multiplier: source.multiplier || 1,
  name: source.name ?? ''
});

export const emptyPoolFormValues = (): PoolFormValues => ({
  conditions: emptyConditionValues(),
  donationFrom: '',
  donationMessage: '',
  enabled: true,
  mainImagePath: null,
  multiplier: 1,
  name: ''
});

export const toPoolSettings = (values: PoolFormValues): PoolSettings => ({
  conditions: toApiConditions(values.conditions),
  donation: {
    from: values.donationFrom.trim() || null,
    message: values.donationMessage.trim() || null
  },
  enabled: values.enabled,
  mainImagePath: values.mainImagePath,
  multiplier: Number(values.multiplier),
  name: values.name.trim()
});

export const isMultiOrganisation = (organizationIds: number[]): boolean =>
  new Set(organizationIds).size > 1;

export const isCurrencyMismatch = (
  currency: string | null | undefined,
  poolCurrency: string | null | undefined
): boolean =>
  !!currency &&
  !!poolCurrency &&
  currency.toUpperCase() !== poolCurrency.toUpperCase();

/** The i18n keys for a failed request: one per recognised API error code, or the fallback. */
export const errorMessageKeys = (
  error: unknown,
  fallbackKey: string
): string[] => {
  const codes = error instanceof GiftMatchingPoolApiError ? error.codes : [];
  const keys = Array.from(
    new Set(
      codes
        .map((code) => apiErrorMessageKeys[code])
        .filter((key): key is string => !!key)
    )
  );
  return keys.length > 0 ? keys : [fallbackKey];
};

export const accountLabel = (
  account: AccountBasic | null | undefined
): string | null => {
  if (!account) {
    return null;
  }
  const name = [account.firstName, account.lastName]
    .filter((part) => !!part)
    .join(' ')
    .trim();
  return name || account.email || null;
};
